import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import { getMembersList } from '../../../services/members';

const MembersContext = createContext();

const isBodyEmpty = (body) => {
  if (body === null || body === undefined) return true;
  if (Array.isArray(body)) return body.length === 0;
  if (typeof body === 'object') return Object.keys(body).length === 0;
  return false;
};

const initialState = {
  baseMembers: [],
  baseMeta: null,
  baseKnowledgebaseUuid: null,
  members: [],
  membersMeta: null,
  loading: false,
  error: null,
  isEmpty: false,
};

export const MembersProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const fetchMembers = useCallback(
    async (params = {}, options = {}) => {
      const { persistAsBase = false } = options;
      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        isEmpty: false,
      }));

      try {
        const response = await getMembersList(params);

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        const body = Array.isArray(response.body) ? response.body : [];
        const isEmpty = isBodyEmpty(body);
        const knowledgebaseUuid = params?.knowledge_base_uuid ?? null;

        if (isEmpty) {
          setState((prev) => ({
            ...prev,
            baseMembers: persistAsBase ? [] : prev.baseMembers,
            baseMeta: persistAsBase ? null : prev.baseMeta,
            baseKnowledgebaseUuid: persistAsBase ? null : prev.baseKnowledgebaseUuid,
            members: [],
            membersMeta: response.meta || null,
            loading: false,
            error: null,
            isEmpty: true,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            baseMembers: persistAsBase ? body : prev.baseMembers,
            baseMeta: persistAsBase ? (response.meta || null) : prev.baseMeta,
            baseKnowledgebaseUuid: persistAsBase ? knowledgebaseUuid : prev.baseKnowledgebaseUuid,
            members: body,
            membersMeta: response.meta || null,
            loading: false,
            error: null,
            isEmpty: false,
          }));
        }
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error('Error fetching members:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          members: [],
          membersMeta: null,
          isEmpty: false,
          ...(persistAsBase
            ? {
                baseMembers: [],
                baseMeta: null,
                baseKnowledgebaseUuid: null,
              }
            : {}),
        }));
      }
    },
    []
  );

  const resetMembersToBase = useCallback(() => {
    fetchRequestIdRef.current = fetchRequestIdRef.current + 1;

    setState((prev) => ({
      ...prev,
      members: prev.baseMembers,
      membersMeta: prev.baseMeta,
      loading: false,
    }));
  }, []);

  const resetMembers = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const refreshMembers = useCallback(
    async (knowledgeBaseUuid, params = {}) => {
      if (!knowledgeBaseUuid) return;
      
      return fetchMembers({
        knowledge_base_uuid: knowledgeBaseUuid,
        page: 1,
        page_size: 100,
        ...params,
      }, { persistAsBase: true });
    },
    [fetchMembers]
  );

  const removeMemberFromState = useCallback((memberUuid) => {
    setState((prev) => ({
      ...prev,
      members: prev.members.filter(
        (member) => (member.memberUuid || member.member_uuid) !== memberUuid
      ),
      baseMembers: prev.baseMembers.filter(
        (member) => (member.memberUuid || member.member_uuid) !== memberUuid
      ),
      // Actualizar meta si es necesario
      membersMeta: prev.membersMeta
        ? {
            ...prev.membersMeta,
            pagination: prev.membersMeta.pagination
              ? {
                  ...prev.membersMeta.pagination,
                  total_items: Math.max(0, (prev.membersMeta.pagination.total_items || 0) - 1),
                  totalItems: Math.max(0, (prev.membersMeta.pagination.totalItems || 0) - 1),
                }
              : prev.membersMeta.pagination,
          }
        : null,
      baseMeta: prev.baseMeta
        ? {
            ...prev.baseMeta,
            pagination: prev.baseMeta.pagination
              ? {
                  ...prev.baseMeta.pagination,
                  total_items: Math.max(0, (prev.baseMeta.pagination.total_items || 0) - 1),
                  totalItems: Math.max(0, (prev.baseMeta.pagination.totalItems || 0) - 1),
                }
              : prev.baseMeta.pagination,
          }
        : null,
    }));
  }, []);

  const updateMemberInState = useCallback((memberUuid, updates) => {
    setState((prev) => ({
      ...prev,
      members: prev.members.map((member) =>
        (member.memberUuid || member.member_uuid) === memberUuid
          ? { ...member, ...updates }
          : member
      ),
      baseMembers: prev.baseMembers.map((member) =>
        (member.memberUuid || member.member_uuid) === memberUuid
          ? { ...member, ...updates }
          : member
      ),
    }));
  }, []);

  const value = {
    baseMembers: state.baseMembers,
    baseMeta: state.baseMeta,
    baseKnowledgebaseUuid: state.baseKnowledgebaseUuid,
    members: state.members,
    membersMeta: state.membersMeta,
    loading: state.loading,
    error: state.error,
    isEmpty: state.isEmpty,
    fetchMembers,
    resetMembersToBase,
    resetMembers,
    refreshMembers,
    removeMemberFromState,
    updateMemberInState,
  };

  return (
    <MembersContext.Provider value={value}>
      {children}
    </MembersContext.Provider>
  );
};

export const useMembersContext = () => {
  const context = useContext(MembersContext);
  if (!context) {
    throw new Error('useMembersContext must be used within MembersProvider');
  }
  return context;
};

